<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import { get } from 'svelte/store'
  import InlinePanel from '../editor/InlinePanel.svelte'
  import AIActionCard from '../AIActionCard.svelte'
  import { inlineViewStore, type InlineView } from '$lib/stores/inlineView'
  import { settingsStore } from '$lib/stores/settings'
  import { AIService } from '$lib/services/AIService'
  import { undoRedoService } from '$lib/services/UndoRedoService'
  import type { Segment } from '$lib/stores/aiFeatures'
  import { loadingIndicatorService } from '$lib/services/LoadingIndicatorService'
  
  // Utility to maintain scroll position and focus on work area
  function maintainScrollPosition(textarea: HTMLTextAreaElement, targetPosition: number, options?: { 
    ensureVisible?: boolean, 
    centerIfNeeded?: boolean 
  }) {
    if (!textarea) return
    
    const { ensureVisible = true, centerIfNeeded = false } = options || {}
    
    // Calculate the position in the text
    const textBeforePosition = textarea.value.substring(0, targetPosition)
    const lines = textBeforePosition.split('\n')
    const targetLine = lines.length
    
    // Get accurate line height and visible area
    const computedStyle = window.getComputedStyle(textarea)
    const lineHeight = parseFloat(computedStyle.lineHeight) || 
                      parseFloat(computedStyle.fontSize) * 1.5 || 
                      20
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0
    const visibleHeight = textarea.clientHeight - paddingTop - paddingBottom
    const visibleLines = Math.floor(visibleHeight / lineHeight)
    
    // Calculate current scroll position in lines
    const currentScrollLines = Math.floor(textarea.scrollTop / lineHeight)
    
    // Determine if we need to scroll
    const targetIsAboveView = targetLine < currentScrollLines + 2 // Keep 2 lines margin at top
    const targetIsBelowView = targetLine > currentScrollLines + visibleLines - 3 // Keep 3 lines margin at bottom
    
    if (ensureVisible && (targetIsAboveView || targetIsBelowView || centerIfNeeded)) {
      let newScrollTop: number
      
      if (centerIfNeeded || (targetIsAboveView && targetIsBelowView)) {
        // Center the target line in the viewport
        newScrollTop = Math.max(0, (targetLine - Math.floor(visibleLines / 2)) * lineHeight)
      } else if (targetIsAboveView) {
        // Scroll up to show the target with margin
        newScrollTop = Math.max(0, (targetLine - 2) * lineHeight)
      } else {
        // Scroll down to show the target with margin
        newScrollTop = Math.max(0, (targetLine - visibleLines + 3) * lineHeight)
      }
      
      // Use instant scrolling for immediate feedback
      textarea.scrollTop = newScrollTop
    }
    
    // Set cursor position for better tracking
    textarea.setSelectionRange(targetPosition, targetPosition)
  }
  
  interface Props {
    editorText: string
    tipTapEditor?: any
    viewMode?: 'source' | 'wysiwyg'
    onTextReplace: (start: number, end: number, newText: string, skipScrollAdjustment?: boolean) => void
    onStatusMessage?: (message: string) => void
  }
  
  let {
    editorText,
    tipTapEditor,
    viewMode = 'source',
    onTextReplace,
    onStatusMessage
  }: Props = $props()
  
  let inlineViews = $state<InlineView[]>([])
  let unsubscribe: (() => void) | null = null
  
  // Subscribe to inline view store
  $effect(() => {
    unsubscribe = inlineViewStore.subscribe(state => {
      inlineViews = state.views
    })
    
    return () => {
      if (unsubscribe) unsubscribe()
    }
  })
  
  /**
   * Process selected text with custom prompt
   */
  export async function processSelectionWithPrompt(
    text: string,
    start: number,
    end: number,
    prompt: string,
    model?: string,
    mode?: 'sentence' | 'paragraph'
  ) {
    // Prevent duplicate requests
    const currentState = get(inlineViewStore)
    if (currentState.isProcessing) {
      onStatusMessage?.('이미 처리 중입니다')
      return
    }
    
    // Create a view for the selection
    const viewType = 'card' // Always use card for prompt-based operations
    
    inlineViewStore.addView({
      originalText: text,
      position: { start, end },
      mode: 'prompt', // Use prompt mode for custom prompts
      type: viewType,
      customPrompt: prompt // Store the prompt
    })
    
    // Get the newly created view
    const state = get(inlineViewStore)
    const newView = state.views[state.views.length - 1]
    
    if (!newView) return
    
    // Set processing state
    inlineViewStore.updateView(newView.id, { status: 'processing' })
    inlineViewStore.setProcessing(true)
    
    // Start with empty history - will be populated when AI generates result
    const initialSegment: Segment = {
      id: newView.id,
      original: text,
      suggested: '',
      hasChanges: false,
      mode: 'prompt',
      reason: `AI가 "${prompt}" 처리 중...`,
      status: 'pending',
      selected: false,
      position: { start, end },
      history: [],  // Empty history initially
      historyIndex: -1,  // No history yet
      customPrompt: prompt
    }
    
    inlineViewStore.updateView(newView.id, {
      segments: [initialSegment]
    })
    
    try {
      // Get context around the selection (200 chars before and after for better context)
      const contextBefore = editorText.substring(Math.max(0, start - 200), start)
      const contextAfter = editorText.substring(end, Math.min(editorText.length, end + 200))
      
      // Build AI prompt based on user request
      const response = await AIService.processWithPrompt(text, prompt, {
        contextBefore,
        contextAfter,
        mode,
        modelId: model
      })

      // Check if the response indicates rate limit exceeded
      if (response && !response.success && response.error &&
          (response.error.includes('한도') || response.error.includes('limit'))) {
        // Rate limit exceeded - upgrade modal is already triggered by AIService
        // Clean up the view and return without showing error message
        inlineViewStore.updateView(newView.id, { status: 'cancelled' })
        inlineViewStore.setProcessing(false)
        return
      }

      const result = response?.success && response?.result ? response.result : null

      if (result && result.trim() !== '') {
        // Add to history and update view
        inlineViewStore.addToHistory(newView.id, result, prompt, text, 'prompt')
        
        // Get updated view to get the correct history
        const updatedState = get(inlineViewStore)
        const updatedView = updatedState.views.find(v => v.id === newView.id)
        
        inlineViewStore.updateView(newView.id, {
          suggestedText: result,
          status: 'completed'
        })
        
        // Update segment for card display with correct history
        const segment: Segment = {
          id: newView.id,
          original: text,
          suggested: result,
          hasChanges: result !== text,
          mode: 'prompt',
          reason: '', // Don't show prompt as reason - customPrompt is used for display
          status: 'pending',
          selected: false,
          position: { start, end },
          history: updatedView?.history || [],
          historyIndex: updatedView?.historyIndex ?? 0,
          customPrompt: prompt
        }
        
        inlineViewStore.updateView(newView.id, {
          segments: [segment]
        })
      } else {
        // Handle empty or invalid response
        throw new Error('AI 서버에서 유효한 응답을 받지 못했습니다')
      }
    } catch (error) {
      console.error('Failed to process text with prompt:', error)
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류'
      onStatusMessage?.(`AI 처리 실패: ${errorMessage}. 재시도 버튼을 클릭하세요.`)
      
      // Create retry callback
      const retryCallback = async () => {
        // Clear error state
        const segment = {
          ...initialSegment,
          error: undefined,
          retryCallback: undefined,
          isProcessing: true
        }
        
        inlineViewStore.updateView(newView.id, {
          segments: [segment],
          status: 'processing'
        })
        
        // Retry the same request
        await processSelectionWithPrompt(text, start, end, prompt, model, mode)
      }
      
      // Update segment with error state
      const errorSegment: Segment = {
        ...initialSegment,
        error: errorMessage,
        retryCallback,
        hasChanges: false,
        reason: '',
        status: 'pending'
      }
      
      inlineViewStore.updateView(newView.id, {
        segments: [errorSegment],
        status: 'cancelled'
      })
    } finally {
      inlineViewStore.setProcessing(false)
    }
  }
  
  /**
   * Process text generation with AI
   */
  export async function processGeneration(
    prompt: string,
    position: number,
    model?: string,
    mode?: 'sentence' | 'paragraph'
  ) {
    // Prevent duplicate requests
    const currentState = get(inlineViewStore)
    if (currentState.isProcessing) {
      onStatusMessage?.('이미 처리 중입니다')
      return
    }
    
    // Check if we're in WYSIWYG mode
    const isWysiwyg = viewMode === 'wysiwyg' && tipTapEditor
    
    // Adjust position for YAML offset in WYSIWYG mode
    let contextPosition = position
    let contextText = editorText
    
    if (isWysiwyg) {
      // In WYSIWYG mode, the position includes YAML offset
      // We need to extract context from text without YAML
      const yamlMatch = editorText.match(/^---\n[\s\S]*?\n---\n/)
      if (yamlMatch) {
        const yamlOffset = yamlMatch[0].length
        contextText = editorText.substring(yamlOffset)
        contextPosition = position - yamlOffset
      }
    }
    
    // Get context around the generation point (1000 chars before for better context)
    // Note: We only use text before cursor for generation context
    const contextBefore = contextText.substring(Math.max(0, contextPosition - 1000), contextPosition)
    
    // Get textarea for source mode
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement
    
    // Show loading indicator at cursor position
    let loadingIndicator: HTMLElement | null = null
    let initialScrollTop = 0
    
    if (isWysiwyg && tipTapEditor) {
      // For TipTap editor, show loading indicator at cursor position
      const editor = (tipTapEditor as any).editor
      if (editor) {
        // Get cursor position in the editor
        const { from } = editor.state.selection
        const coords = editor.view.coordsAtPos(from)
        
        // Create loading indicator element
        loadingIndicator = document.createElement('div')
        loadingIndicator.className = 'continuation-loading-indicator'
        loadingIndicator.style.cssText = `
          position: fixed;
          left: ${coords.left}px;
          top: ${coords.top}px;
          z-index: 10000;
          pointer-events: none;
        `
        loadingIndicator.innerHTML = `
          <div class="loading-dots">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        `
        document.body.appendChild(loadingIndicator)
        ;(loadingIndicator as any).type = 'wysiwyg'
      }
      onStatusMessage?.('텍스트 생성 중...')
    } else if (textarea) {
      // Save initial scroll position
      initialScrollTop = textarea.scrollTop
      
      // Ensure the generation point is visible before starting
      const textBeforePosition = textarea.value.substring(0, position)
      const linesBeforePosition = textBeforePosition.split('\n').length
      
      const computedStyle = window.getComputedStyle(textarea)
      const lineHeight = parseFloat(computedStyle.lineHeight) || 
                        parseFloat(computedStyle.fontSize) * 1.5 || 
                        20
      const visibleHeight = textarea.clientHeight - parseFloat(computedStyle.paddingTop || '0') - parseFloat(computedStyle.paddingBottom || '0')
      const visibleLines = Math.floor(visibleHeight / lineHeight)
      
      // Position the generation point in the upper-middle area of the viewport (30% from top)
      const targetScrollLine = Math.max(0, linesBeforePosition - Math.floor(visibleLines * 0.3))
      const targetScrollTop = targetScrollLine * lineHeight
      
      // Set scroll position before showing loading indicator
      textarea.scrollTop = targetScrollTop
      
      loadingIndicator = loadingIndicatorService.showGenerationLoading(textarea, position)
    }
    
    // Set processing state
    inlineViewStore.setProcessing(true)
    onStatusMessage?.('텍스트 생성 중...')
    
    try {
      // Save current state to undo history before applying
      undoRedoService.addEntry(
        'edit',
        editorText,
        new Map(),
        {
          description: 'AI 생성 전 원본',
          previousText: editorText
        }
      )
      
      let streamedText = ''
      let isFirstChunk = true
      
      // For WYSIWYG mode, set cursor to correct position before streaming
      if (isWysiwyg && tipTapEditor) {
        const editor = (tipTapEditor as any).editor
        if (editor) {
          // Set cursor to the generation position before starting streaming
          editor.commands.focus()
          editor.commands.setTextSelection(position)
        }
      }

      const startTime = Date.now()

      // Call AI generation service with streaming and context
      // Note: afterText is intentionally not passed - we want to generate
      // continuation based only on what came before the cursor
      const response = await AIService.generateStreaming(
        prompt,
        {
          beforeText: contextBefore,
          cursorPosition: position,
          mode,
          modelId: model
        },
        (chunk: string) => {
          // Handle each chunk of streamed text
          if (isFirstChunk) {
            // Hide loading indicator on first chunk
            if (isWysiwyg && loadingIndicator && (loadingIndicator as any).type === 'wysiwyg') {
              // Remove the loading indicator in WYSIWYG mode
              loadingIndicator.remove()
            } else if (loadingIndicator) {
              loadingIndicatorService.hideGenerationLoading(loadingIndicator)
            }
            isFirstChunk = false
          }

          // Update text in real-time
          streamedText += chunk

          // Handle streaming differently for WYSIWYG vs source mode
          if (isWysiwyg) {
            // For TipTap editor, use replaceText with streaming mode (-1)
            // This will handle escaped markdown properly
            if (tipTapEditor) {
              tipTapEditor.replaceText(-1, -1, chunk)
            }
          } else {
            // For source mode, replace text with accumulated stream
            const previousLength = streamedText.length - chunk.length
            const savedScrollTop = textarea ? textarea.scrollTop : 0
            
            // Replace text in editor with accumulated stream (skip scroll adjustment, we'll handle it)
            onTextReplace(position, position + previousLength, streamedText, true)
            
            // Auto-scroll to keep generated text visible during streaming
            // Use a double requestAnimationFrame to ensure DOM is fully updated
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (textarea) {
                const newCursorPosition = position + streamedText.length
                
                // Calculate where the generation point should be visible
                const textBeforeGeneration = textarea.value.substring(0, position)
                const linesBeforeGeneration = textBeforeGeneration.split('\n').length
                
                const computedStyle = window.getComputedStyle(textarea)
                const lineHeight = parseFloat(computedStyle.lineHeight) || 
                                  parseFloat(computedStyle.fontSize) * 1.5 || 
                                  20
                const paddingTop = parseFloat(computedStyle.paddingTop) || 0
                const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0
                const visibleHeight = textarea.clientHeight - paddingTop - paddingBottom
                const visibleLines = Math.floor(visibleHeight / lineHeight)
                
                // Calculate the ideal scroll position to keep generation point in the middle-lower part of view
                const targetScrollLine = Math.max(0, linesBeforeGeneration - Math.floor(visibleLines * 0.3))
                const targetScrollTop = targetScrollLine * lineHeight
                
                // Only adjust scroll if the generation area is not visible or if scroll jumped unexpectedly
                const currentScrollTop = textarea.scrollTop
                const scrollJumped = Math.abs(currentScrollTop - savedScrollTop) > visibleHeight * 0.5
                
                if (scrollJumped || Math.abs(currentScrollTop - targetScrollTop) > lineHeight * 3) {
                  textarea.scrollTop = targetScrollTop
                }
                
                // Update cursor position
                textarea.setSelectionRange(newCursorPosition, newCursorPosition)
                }
              })
            })
          }
        }
      )
      
      // Use the streamed text as the final result if we have it
      // The response.result might be empty for streamed responses
      const finalText = streamedText || (response?.success && response?.result ? response.result : null)
      
      if (finalText) {
        // For streaming in WYSIWYG mode, finalize markdown formatting
        if (streamedText && isWysiwyg && tipTapEditor) {
          tipTapEditor.finalizeStreamingContent()
        }
        
        // For streaming, text has already been inserted chunk-by-chunk
        // For non-streaming, we need to insert the result now
        if (!streamedText && response?.result) {
          onTextReplace(position, position, response.result, true) // Skip scroll, we're handling it
        }
        
        // Build the final text state
        // For streaming: text was already inserted, so use current editor text
        // For non-streaming: we just inserted it above
        const beforeText = editorText.substring(0, position)
        const afterText = editorText.substring(position + (streamedText ? streamedText.length : 0))
        const fullNewText = beforeText + finalText + afterText
        
        // Save the new state to undo history
        undoRedoService.addEntry(
          'edit',
          fullNewText,
          new Map(),
          {
            description: `AI 텍스트 생성: ${prompt}`,
            previousText: ''
          }
        )
        
        // Hide loading indicator if still showing
        if (loadingIndicator && isFirstChunk) {
          if ((loadingIndicator as any).type === 'wysiwyg') {
            loadingIndicator.remove()
          } else {
            loadingIndicatorService.hideGenerationLoading(loadingIndicator)
          }
        }
        
        onStatusMessage?.('텍스트가 생성되었습니다')
      } else {
        // Reset streaming state on failure
        if (isWysiwyg && tipTapEditor) {
          tipTapEditor.resetStreamingState()
        }
        
        // Hide loading indicator on failure
        if (loadingIndicator) {
          if ((loadingIndicator as any).type === 'wysiwyg') {
            loadingIndicator.remove()
          } else {
            loadingIndicatorService.hideGenerationLoading(loadingIndicator)
          }
        }
        onStatusMessage?.('텍스트 생성에 실패했습니다')
      }
    } catch (error) {
      // Reset streaming state on error
      if (isWysiwyg && tipTapEditor) {
        tipTapEditor.resetStreamingState()
      }
      
      // Hide loading indicator on error
      if (loadingIndicator) {
        if ((loadingIndicator as any).type === 'wysiwyg') {
          loadingIndicator.remove()
        } else {
          loadingIndicatorService.hideGenerationLoading(loadingIndicator)
        }
      }
      console.error('Failed to generate text:', error)
      onStatusMessage?.('AI 생성 중 오류가 발생했습니다')
    } finally {
      inlineViewStore.setProcessing(false)
    }
  }
  
  /**
   * Process selected text with AI
   */
  export async function processSelection(
    text: string,
    start: number,
    end: number,
    mode: 'paraphrase' | 'expand' | 'shrink' | 'continue',
    element?: HTMLElement,
    model?: string
  ) {
    // Use provided model or get from settings
    const modelToUse = model || get(settingsStore).ai?.selectedModel
    
    // Get context around the selected text (100 chars before and after)
    const contextBefore = editorText.substring(Math.max(0, start - 100), start)
    const contextAfter = editorText.substring(end, Math.min(editorText.length, end + 100))
    const textWithContext = {
      selected: text,
      before: contextBefore,
      after: contextAfter,
      fullText: contextBefore + text + contextAfter
    }
    // Check if view already exists for this position
    const existingView = inlineViewStore.getViewByPosition(start, end)
    let viewToProcess: InlineView | undefined
    
    if (existingView) {
      // Use the existing view but update its mode
      viewToProcess = existingView
      
      // Don't update originalText - that should remain the true original
      // The 'text' parameter contains the current text being processed
      
      inlineViewStore.updateView(existingView.id, { 
        mode,
        status: 'processing'
        // Keep originalText unchanged - it's the true original selection
      })
      inlineViewStore.setActiveView(existingView.id)
    } else {
      // Determine view type based on text length
      const viewType = inlineViewStore.determineViewType(text)
      
      // Don't save to history here - only save when actually applying changes
      // Card creation doesn't change text, only shows UI
      
      // Add new view
      inlineViewStore.addView({
        originalText: text,
        position: { start, end, element },
        mode,
        type: viewType
      })
      
      // Get the newly created view
      const state = get(inlineViewStore)
      viewToProcess = state.views[state.views.length - 1]
      
      // Don't add initial state to history here - addView already handles it
    }
    
    if (!viewToProcess) return
    
    const newView = viewToProcess
    
    // Set processing state
    inlineViewStore.updateView(newView.id, { status: 'processing' })
    inlineViewStore.setProcessing(true)
    
    // If it's a card view, create initial segment for immediate display
    if (newView.type === 'card') {
      const initialSegment: Segment = {
        id: newView.id,
        original: text,  // This is the current text being processed
        suggested: '',
        hasChanges: false,
        mode,
        reason: 'AI가 분석 중...',
        status: 'pending',
        selected: false
      }
      
      inlineViewStore.updateView(newView.id, {
        segments: [initialSegment]
      })
    }
    
    try {
      // Call appropriate AI service based on mode with context
      let response: any
      
      // For short text, provide context to AI for better understanding
      const hasContext = contextBefore || contextAfter
      
      // Check if text is in middle of sentence (has context after)
      const isMiddleOfSentence = contextAfter && contextAfter.trim().length > 0
      
      // Only use contextPrompt for very short selections (less than 100 chars)
      // For longer selections, process the full text directly
      const useContextPrompt = hasContext && text.length < 100
      
      const contextPrompt = useContextPrompt ? 
        `다음 문맥에서 [대상] 부분만 ${mode === 'paraphrase' ? '개선' : mode === 'expand' ? '확장' : mode === 'shrink' ? '축약' : '계속'}해주세요:\n\n` +
        `${contextBefore}[대상: ${text}]${contextAfter}\n\n` +
        `규칙:\n` +
        `1. [대상] 부분만 수정하세요\n` +
        `2. 앞뒤 문맥과 자연스럽게 이어지도록 하세요\n` +
        `3. 원본 텍스트의 따옴표, 구두점 등 모든 문장 부호를 유지하세요\n` +
        (isMiddleOfSentence ? `4. 문장을 끝맺지 마세요 (마침표, 느낌표 등 추가 금지)\n` : '') +
        `${isMiddleOfSentence ? '5' : '4'}. 수정된 텍스트만 반환하세요\n\n` +
        `수정된 텍스트:` : 
        null
      
      switch (mode) {
        case 'paraphrase':
          response = await AIService.paraphrase(contextPrompt || text, { modelId: modelToUse })
          break
        case 'expand':
          response = await AIService.expand(contextPrompt || text, { modelId: modelToUse })
          break
        case 'shrink':
          response = await AIService.shrink(contextPrompt || text, { modelId: modelToUse })
          break
        case 'continue':
          // Continue uses original text since it needs to continue from there
          response = await AIService.continueWriting(text, { mode: 'sentence', modelId: modelToUse })
          break
      }
      
      // Check if the response indicates rate limit exceeded
      if (response && !response.success && response.error &&
          (response.error.includes('한도') || response.error.includes('limit'))) {
        // Rate limit exceeded - upgrade modal is already triggered by AIService
        // Clean up the view and return without showing error message
        inlineViewStore.updateView(newView.id, { status: 'cancelled' })
        inlineViewStore.setProcessing(false)
        return
      }

      const result = response?.success && response?.result ? response.result : null

      if (result && result.trim() !== '') {
        // Get the base text for this operation (what was processed)
        const baseTextForHistory = text  // The text that was sent to AI

        // For continue mode, append the generated text to the original
        const finalResult = mode === 'continue' ? text + result : result
        
        // Get the proper reason based on mode
        let reason = ''
        switch (mode) {
          case 'paraphrase':
            reason = '개선하기'
            break
          case 'expand':
            reason = '늘려쓰기'
            break
          case 'shrink':
            reason = '줄여쓰기'
            break
          case 'continue':
            reason = '이어쓰기'
            break
          default:
            reason = `AI ${mode}`
        }
        
        // Add to history and update view
        inlineViewStore.addToHistory(newView.id, finalResult, reason, baseTextForHistory, mode)
        
        // Get updated view to get the correct history
        const updatedState = get(inlineViewStore)
        const updatedView = updatedState.views.find(v => v.id === newView.id)
        
        inlineViewStore.updateView(newView.id, {
          suggestedText: finalResult,
          status: 'completed'
        })
        
        // If it's a card view, update segment for card display
        if (newView.type === 'card') {
          const segment: Segment = {
            id: newView.id,
            original: baseTextForHistory,  // The text that was actually processed by AI
            suggested: finalResult,
            hasChanges: finalResult !== baseTextForHistory,
            mode,
            reason: '',  // No reason needed for inline cards
            status: 'pending',
            selected: false,
            position: { start, end },
            history: updatedView?.history || [],
            historyIndex: updatedView?.historyIndex ?? 0
          }
          
          inlineViewStore.updateView(newView.id, {
            segments: [segment]
          })
        }
      } else {
        // Handle empty or invalid response
        throw new Error('AI 서버에서 유효한 응답을 받지 못했습니다')
      }
    } catch (error) {
      console.error('Failed to process text:', error)
      onStatusMessage?.('AI 처리 중 오류가 발생했습니다')
      inlineViewStore.updateView(newView.id, { status: 'cancelled' })
    } finally {
      inlineViewStore.setProcessing(false)
    }
  }
  
  /**
   * Apply inline view changes
   */
  export function handleApplyView(viewId: string, text: string) {
    const view = inlineViews.find(v => v.id === viewId)
    if (!view) return
    
    // Check if already applied - prevent duplicate applications
    if (view.status === 'applied') {
      return
    }
    
    // Get textarea element for scroll management
    const textarea = document.querySelector('textarea.base-editor') as HTMLTextAreaElement || 
                     document.querySelector('textarea') as HTMLTextAreaElement
    
    // Save current scroll position
    const currentScrollTop = textarea?.scrollTop || 0
    
    // Check if we need to save the current state first
    const currentHistoryEntry = undoRedoService.getCurrentEntry()
    const needToSaveCurrentState = !currentHistoryEntry || currentHistoryEntry.text !== editorText
    
    // Save current state if it's not in history yet
    // This happens when applying the first card after creation
    if (needToSaveCurrentState) {
      undoRedoService.addEntry(
        'edit',
        editorText,  // Current state before any changes
        new Map(),
        {
          description: 'AI 작업 적용 전 원본',
          previousText: editorText
        }
      )
    }
    
    // Calculate the new full text after replacement
    // For generation (originalText is empty), insert at position
    const isGeneration = view.originalText === ''
    const beforeText = editorText.substring(0, view.position.start)
    const afterText = isGeneration 
      ? editorText.substring(view.position.start) // Don't skip any text for insertion
      : editorText.substring(view.position.end) // Skip replaced text for replacement
    const fullNewText = beforeText + text + afterText
    
    // Save the new state to undo history
    undoRedoService.addEntry(
      'edit',
      fullNewText,  // Save the complete text after replacement
      new Map(),
      {
        description: `인라인 ${view.mode} 적용`,
        previousText: view.originalText
      }
    )
    
    // Replace text in editor
    // For WYSIWYG mode, positions are TipTap native coordinates (no YAML offset)
    // For source mode, positions include full text with YAML
    const skipYamlAdjustment = viewMode === 'wysiwyg'
    onTextReplace(view.position.start, view.position.end, text, skipYamlAdjustment)
    
    // Mark view as applied
    inlineViewStore.applyView(viewId)
    
    // Clean up applied views after a short delay to ensure DOM updates
    setTimeout(() => {
      inlineViewStore.clearAppliedViews()
    }, 100)
    
    // Maintain scroll position to keep the applied area visible
    if (textarea) {
      // Calculate the new cursor position after applying changes
      const newCursorPosition = view.position.start + text.length
      
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        // Keep the applied area visible, centering if it was a large change
        const shouldCenter = Math.abs(text.length - (view.position.end - view.position.start)) > 100
        maintainScrollPosition(textarea, newCursorPosition, {
          ensureVisible: true,
          centerIfNeeded: shouldCenter
        })
      })
    }
    
    onStatusMessage?.('변경사항이 적용되었습니다')
  }
  
  /**
   * Cancel inline view
   */
  export function handleCancelView(viewId: string) {
    inlineViewStore.cancelView(viewId)
    onStatusMessage?.('취소되었습니다')
  }
  
  /**
   * Handle text change in inline panel
   */
  function handleTextChange(viewId: string, text: string) {
    // For manual edits, find the current base text
    const view = inlineViews.find(v => v.id === viewId)
    if (view) {
      const currentHistory = view.history
      const currentIndex = view.historyIndex ?? (currentHistory ? currentHistory.length - 1 : 0)
      const baseText = currentHistory && currentIndex >= 0 
        ? currentHistory[currentIndex].baseText 
        : view.originalText
      inlineViewStore.addToHistory(viewId, text, 'Manual edit', baseText, undefined)  // No mode for manual edits
    }
  }
  
  /**
   * Clear all applied views
   */
  export function clearAppliedViews() {
    inlineViewStore.clearAppliedViews()
  }
  
  /**
   * Clear all views
   */
  export function clearAll() {
    inlineViewStore.clearAll()
  }
  
  /**
   * Get card views for sidebar display
   */
  export function getCardViews() {
    return inlineViews.filter(v => v.type === 'card' && v.status !== 'applied')
  }
  
  // Clean up on unmount
  onDestroy(() => {
    if (unsubscribe) unsubscribe()
  })
</script>

<!-- Render inline panels - TEMPORARILY DISABLED for short text focus -->
<!-- {#each inlineViews.filter(v => v.type === 'panel' && v.status !== 'applied') as view (view.id)}
  <InlinePanel
    {view}
    onApply={(text) => handleApplyView(view.id, text)}
    onCancel={() => handleCancelView(view.id)}
    onTextChange={(text) => handleTextChange(view.id, text)}
  />
{/each} -->

<!-- Card views will be displayed in the sidebar -->
{#snippet cardViews()}
  {#each inlineViews.filter(v => v.type === 'card' && v.status !== 'applied') as view, index}
    {#if view.segments?.[0]}
      <AIActionCard
        segment={view.segments[0]}
        {index}
        mode={view.mode}
        customPrompt={view.customPrompt}
        showIndex={false}
        onApply={() => {
          if (view.suggestedText) {
            handleApplyView(view.id, view.suggestedText)
          }
        }}
        onIgnore={() => handleCancelView(view.id)}
        onParaphrase={() => {
          // Use suggested text if available (from previous operation), otherwise use original
          const textToProcess = view.suggestedText || view.originalText
          processSelection(
            textToProcess,
            view.position.start,
            view.position.end,
            'paraphrase'
          )
        }}
        onExpand={() => {
          const textToProcess = view.suggestedText || view.originalText
          processSelection(
            textToProcess,
            view.position.start,
            view.position.end,
            'expand'
          )
        }}
        onShrink={() => {
          const textToProcess = view.suggestedText || view.originalText
          processSelection(
            textToProcess,
            view.position.start,
            view.position.end,
            'shrink'
          )
        }}
        onContinue={() => {
          const textToProcess = view.suggestedText || view.originalText
          processSelection(
            textToProcess,
            view.position.start,
            view.position.end,
            'continue'
          )
        }}
      />
    {/if}
  {/each}
{/snippet}

